// Money input (DESIGN_SYSTEM §9.3). "Rs" prefix, inputmode=decimal, ledger figures, live lakh
// grouping with the caret preserved, Devanagari digits accepted, at most 2 decimals, min/max in the
// helper text, and the amount in words from one lakh up (catches extra-zero mistakes). The value is
// integer paisa; display strings never leave the component.
import { forwardRef, useLayoutEffect, useRef, useState, type ChangeEvent } from "react";
import {
  amountInWords,
  formatMoney,
  formatMoneyInput,
  normalizeDigits,
  parseMoney,
  type MoneyParseResult,
} from "@ady/format";
import { t } from "@ady/i18n";
import { Field, type FieldProps } from "./Field";
import { Input, type InputProps } from "./Input";

const ONE_LAKH_PAISA = 10_000_000;

export interface MoneyInputProps extends Omit<
  InputProps,
  "value" | "onChange" | "prefix" | "type" | "min" | "max"
> {
  /** Integer paisa, or null when empty. */
  value: number | null;
  onValueChange: (paisa: number | null, result: MoneyParseResult) => void;
}

/** Index in `text` after the `n`-th digit/decimal-point character (for caret restoration). */
function caretAfterSignificant(text: string, n: number): number {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i++) {
    if (/[\d.]/.test(text[i] ?? "")) seen++;
    if (seen === n) return i + 1;
  }
  return text.length;
}
const significantBefore = (text: string, caret: number) =>
  (text.slice(0, caret).match(/[\d.]/g) ?? []).length;

export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { value, onValueChange, ...rest },
  forwarded,
) {
  const inner = useRef<HTMLInputElement | null>(null);
  const [text, setText] = useState(() =>
    value === null ? "" : formatMoney(value, { bare: true }),
  );
  const [lastValue, setLastValue] = useState(value);
  const pendingCaret = useRef<number | null>(null);
  if (value !== lastValue) {
    setLastValue(value);
    const parsed = parseMoney(text);
    const same = parsed.ok ? parsed.paisa === value : value === null;
    if (!same) setText(value === null ? "" : formatMoney(value, { bare: true }));
  }
  useLayoutEffect(() => {
    if (
      pendingCaret.current !== null &&
      inner.current &&
      document.activeElement === inner.current
    ) {
      inner.current.setSelectionRange(pendingCaret.current, pendingCaret.current);
    }
    pendingCaret.current = null;
  }, [text]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    const keep = significantBefore(normalizeDigits(raw), caret);
    const next = formatMoneyInput(raw);
    pendingCaret.current = caretAfterSignificant(next, keep);
    setText(next);
    const result = parseMoney(next);
    onValueChange(result.ok ? result.paisa : null, result);
  };
  return (
    <Input
      ref={(el) => {
        inner.current = el;
        if (typeof forwarded === "function") forwarded(el);
        else if (forwarded) forwarded.current = el;
      }}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      prefix={t("money.prefix")}
      prefixLabel={t("money.prefixLabel")}
      value={text}
      onChange={handleChange}
      ledger
      {...rest}
    />
  );
});

/** The catalogue message for a parse result plus optional bounds, or undefined when valid. */
export function moneyError(
  result: MoneyParseResult,
  bounds: { min?: number; max?: number } = {},
): string | undefined {
  if (!result.ok) return t(`money.errors.${result.error}`);
  if (bounds.min !== undefined && result.paisa < bounds.min)
    return t("money.errors.below_min", { min: formatMoney(bounds.min) });
  if (bounds.max !== undefined && result.paisa > bounds.max)
    return t("money.errors.above_max", { max: formatMoney(bounds.max) });
  return undefined;
}

export interface MoneyFieldProps extends Omit<FieldProps, "children" | "live" | "help"> {
  value: number | null;
  onValueChange: MoneyInputProps["onValueChange"];
  /** Paisa bounds shown in the helper text. */
  min?: number;
  max?: number;
  /** Refund mode: `max` is the refundable amount ("Up to Rs 4,500 can be refunded"). */
  refund?: boolean;
  inputProps?: Omit<MoneyInputProps, "value" | "onValueChange">;
}

/** Field + MoneyInput with the range helper and the amount-in-words line. */
export function MoneyField({
  value,
  onValueChange,
  min,
  max,
  refund,
  inputProps,
  ...field
}: MoneyFieldProps) {
  const help =
    max !== undefined && refund
      ? t("money.refundCap", { max: formatMoney(max) })
      : min !== undefined && max !== undefined
        ? t("money.range", { min: formatMoney(min), max: formatMoney(max) })
        : max !== undefined
          ? t("money.max", { max: formatMoney(max) })
          : undefined;
  const words =
    value !== null && Math.abs(value) >= ONE_LAKH_PAISA
      ? t("money.inWords", { words: amountInWords(value) })
      : undefined;
  return (
    <Field {...field} help={help} live={words}>
      <MoneyInput value={value} onValueChange={onValueChange} {...inputProps} />
    </Field>
  );
}
